import { useToast } from "@app/components/toast";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useRef, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import StakeholderTxnsPanel from "./stakeholder-txns-panel";

import type { stakeholdersMergePage_Query } from "@app/queries/stakeholdersMergePage_Query.graphql";
import type { stakeholdersMergePage_MergeMutation } from "@app/queries/stakeholdersMergePage_MergeMutation.graphql";
import type { stakeholdersMergePage_RenameMutation } from "@app/queries/stakeholdersMergePage_RenameMutation.graphql";

// Search + sort run server-side so both span every counterparty (the whole point
// of this page is to find a stakeholder across the full set and drag it onto
// another), while only one page of rows is fetched at a time.
export const PAGE_SIZE = 50;

export const stakeholdersMergeQuery = graphql`
  query stakeholdersMergePage_Query(
    $perspectiveId: ID
    $search: String
    $sort: String
    $sortDir: String
    $limit: Int
    $offset: Int
  ) {
    costStakeholders(
      perspectiveId: $perspectiveId
      search: $search
      sort: $sort
      sortDir: $sortDir
      limit: $limit
      offset: $offset
    ) {
      totalCount
      stakeholders {
        id
        name
        total
        txnCount
      }
    }
  }
`;

const mergeMutation = graphql`
  mutation stakeholdersMergePage_MergeMutation(
    $input: StakeholderMergeInputObject!
  ) {
    stakeholderMerge(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const renameMutation = graphql`
  mutation stakeholdersMergePage_RenameMutation(
    $input: StakeholderRenameInputObject!
  ) {
    stakeholderRename(input: $input) {
      stakeholder {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

type SortKey = "name" | "total" | "txnCount";
type SortDir = "asc" | "desc";
type Payload = stakeholdersMergePage_Query["response"]["costStakeholders"];

// The page's SortKey → the server's sort enum.
const SERVER_SORT: Record<SortKey, string> = {
  name: "NAME",
  total: "COST",
  txnCount: "TXNS",
};

export default function StakeholdersMergePage({
  queryRef,
}: {
  queryRef: PreloadedQuery<stakeholdersMergePage_Query>;
}) {
  const preloaded = usePreloadedQuery(stakeholdersMergeQuery, queryRef);
  const toast = useToast();

  const [list, setList] = useState<Payload>(preloaded.costStakeholders);
  useEffect(() => setList(preloaded.costStakeholders), [preloaded]);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "total",
    dir: "asc",
  });
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [drill, setDrill] = useState<{ id: string; name: string } | null>(null);
  // The whole list freezes for the duration of a merge. A second drop landing
  // mid-merge would race the first one's refetch and merge a row that the
  // server has already absorbed.
  const [merging, setMerging] = useState<string | null>(null);
  const mergeLock = useRef(false);
  const frozen = merging !== null;

  const rows = list.stakeholders;
  const totalCount = list.totalCount;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const vars = () => ({
    perspectiveId: getPerspectiveId() ?? undefined,
    search: debouncedQuery.trim() || null,
    sort: SERVER_SORT[sort.key],
    sortDir: sort.dir === "asc" ? "ASC" : "DESC",
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const fetchPage = () => {
    setIsFetching(true);
    return fetchQuery<stakeholdersMergePage_Query>(environment, stakeholdersMergeQuery, vars())
      .toPromise()
      .then((fresh) => fresh && setList(fresh.costStakeholders))
      .catch((error) => window.alert(error.message))
      .finally(() => setIsFetching(false));
  };

  // Refetch on search / sort / page change (skip first render — the loader has page 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, sort, page]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
    setPage(0);
  };

  const arrow = (key: SortKey) =>
    sort.key !== key ? "" : sort.dir === "asc" ? " ↑" : " ↓";

  const merge = (sourceId: string, targetId: string) => {
    // A ref, not `frozen`: two drops can land in the same React batch and both
    // read a pre-freeze render. The lock has to hold before anything re-renders.
    if (sourceId === targetId || mergeLock.current) return;

    const nameOf = (id: string) =>
      rows.find((row) => row.id === id)?.name ?? "counterparty";
    const sourceName = nameOf(sourceId);
    const targetName = nameOf(targetId);

    mergeLock.current = true;
    setMerging(`Merging ${sourceName} into ${targetName}…`);

    const thaw = (message: string, kind: "success" | "error") => {
      mergeLock.current = false;
      setMerging(null);
      toast({ message, kind });
    };

    commitMutation<stakeholdersMergePage_MergeMutation>(environment, {
      mutation: mergeMutation,
      variables: { input: { sourceId, targetId } },
      onCompleted: (response, errors) => {
        const failure =
          errors?.[0]?.message ??
          response.stakeholderMerge?.errors?.[0]?.message;
        if (failure) {
          thaw(failure, "error");
          return;
        }
        // Stay frozen through the refetch: until it lands the rows on screen
        // still show the source, and dropping onto it would merge nothing.
        fetchPage().then(() =>
          thaw(`Merged ${sourceName} into ${targetName}.`, "success")
        );
      },
      onError: (error) =>
        thaw(error.message || "Merge failed — please try again.", "error"),
    });
  };

  const commitRename = (id: string) => {
    const name = draftName.trim();
    setEditingId(null);
    const current = rows.find((r) => r.id === id);
    if (!name || !current || name === current.name) return;
    commitMutation<stakeholdersMergePage_RenameMutation>(environment, {
      mutation: renameMutation,
      variables: { input: { stakeholderId: id, name } },
      onCompleted: (_response, errors) => {
        if (errors?.length) {
          window.alert(errors[0].message);
          return;
        }
        fetchPage();
      },
      onError: (error) => window.alert(error.message),
    });
  };

  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;

  const pager =
    totalCount > PAGE_SIZE ? (
      <div className="flex items-center justify-between text-sm text-gray-500 py-2">
        <span>
          Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasPrev || frozen}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          </span>
          <button
            type="button"
            disabled={!hasNext || frozen}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-bold">Merge stakeholders</h1>
        <span className="text-sm text-gray-500">
          Drag a row onto another to merge it. Click a name to rename.
        </span>
        {(isFetching || frozen) && (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            role="status"
            aria-label="Loading"
          />
        )}
        {merging && (
          <span className="text-sm text-amber-700" aria-live="polite">
            {merging}
          </span>
        )}
      </div>

      <input
        type="text"
        value={query}
        disabled={frozen}
        onChange={(event) => {
          setQuery(event.target.value);
          setPage(0);
        }}
        placeholder="Search by name…"
        className="mb-4 w-72 text-sm border border-gray-300 rounded px-3 py-1.5 disabled:bg-gray-50 disabled:text-gray-400"
      />

      {pager}

      {/* `pointer-events-none` is the freeze: it kills every drag, rename click
          and drill-down on the list at once, so nothing can start a second
          mutation while this one is in flight. */}
      <div
        className={`bg-white border border-gray-200 rounded-lg overflow-hidden transition-opacity duration-200 ${
          isFetching || frozen ? "opacity-50" : ""
        } ${frozen ? "pointer-events-none select-none" : ""}`}
        aria-busy={frozen}
      >
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th
                className="px-3 py-2 text-left font-medium cursor-pointer select-none"
                onClick={() => toggleSort("name")}
              >
                Counterparty{arrow("name")}
              </th>
              <th
                className="px-3 py-2 text-right font-medium cursor-pointer select-none"
                onClick={() => toggleSort("total")}
              >
                Total cost{arrow("total")}
              </th>
              <th
                className="px-3 py-2 text-right font-medium cursor-pointer select-none"
                onClick={() => toggleSort("txnCount")}
              >
                Txns{arrow("txnCount")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="p-4 text-gray-400">
                  No cost counterparties.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                draggable={editingId !== row.id}
                onDragStart={() => setDraggingId(row.id)}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDragOverId(null);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (draggingId && draggingId !== row.id)
                    setDragOverId(row.id);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggingId) merge(draggingId, row.id);
                  setDraggingId(null);
                  setDragOverId(null);
                }}
                className={`hover:bg-gray-50 cursor-move ${
                  dragOverId === row.id
                    ? "bg-amber-100 outline outline-2 outline-amber-400"
                    : ""
                } ${draggingId === row.id ? "opacity-40" : ""}`}
              >
                <td className="px-3 py-2 text-gray-900">
                  {editingId === row.id ? (
                    <input
                      autoFocus
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      onBlur={() => commitRename(row.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") commitRename(row.id);
                        if (event.key === "Escape") setEditingId(null);
                      }}
                      className="text-sm border border-gray-300 rounded px-2 py-1 w-full"
                    />
                  ) : (
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => {
                        setEditingId(row.id);
                        setDraftName(row.name);
                      }}
                    >
                      {row.name}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                  {money(row.total)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                  <button
                    type="button"
                    className="hover:underline text-blue-600 disabled:text-gray-400 disabled:no-underline"
                    disabled={row.txnCount === 0}
                    draggable={false}
                    onClick={() => setDrill({ id: row.id, name: row.name })}
                  >
                    {row.txnCount}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pager}

      {drill && (
        <StakeholderTxnsPanel
          stakeholder={drill}
          onClose={() => setDrill(null)}
          onChanged={fetchPage}
        />
      )}
    </div>
  );
}
